/**
 * 角色权限服务
 * 封装所有角色权限相关的 API 调用和业务逻辑
 */
import { apiClient } from "../../../config/netConfig";
import { message } from "antd";

export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message: string;
}

const request = async <T>(url: string, options?: any): Promise<ApiResponse<T>> => {
  try {
    const response = await apiClient.request({ url, ...options });
    const responseData = response.data;

    if (response.status === 200) {
      if ("success" in responseData && "data" in responseData && "message" in responseData) {
        return responseData;
      }
      return { success: true, data: responseData, message: "操作成功" };
    } else {
      const errorMessage = responseData.message || `请求失败: ${response.status}`;
      message.error(errorMessage);
      return { success: false, data: responseData, message: errorMessage };
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "请求失败";
    message.error(errorMessage);
    throw error;
  }
};

export const RolePermissionService = {
  // 获取角色权限
  getRolePermissions: async (roleId: string): Promise<ApiResponse<{ permissions: any[] }>> => {
    return request(`/role/${roleId}/permissions`);
  },

  // 获取角色权限数量
  getRolePermissionCount: async (roleId: string): Promise<number> => {
    try {
      // console.debug(`🔍 开始获取角色 ${roleId} 的权限数量`);

      const response = await request(`/role/${roleId}/permissions`);
      // console.debug(`📡 角色 ${roleId} 权限接口响应:`, response);

      if (response.success) {
        // 后端返回的数据格式：{ success: true, data: permissions }
        // 其中 data 直接是权限数组，不是 { permissions: [...] }
        let permissions: any[] = [];
        const responseData = response.data as any;

        if (Array.isArray(responseData)) {
          // 新格式：data 直接是数组
          permissions = responseData;
        } else if (responseData && typeof responseData === 'object' && Array.isArray(responseData.permissions)) {
          // 兼容旧格式：{ permissions: [...] }
          permissions = responseData.permissions;
        } else {
          console.warn(`⚠️ 角色 ${roleId} 权限数据格式不正确:`, responseData);
          return 0;
        }

        // console.debug(`📋 角色 ${roleId} 权限列表长度:`, permissions.length);

        // 计算实际的功能权限数量
        // 如果权限数据有 type 字段，排除 MODULE 类型的节点
        // 否则返回所有权限的数量
        let count = permissions.length;
        const hasTypeField = permissions.some((p: any) => p.type !== undefined);
        if (hasTypeField) {
          count = permissions.filter((p: any) => p.type !== "MODULE").length;
        }

        // console.debug(`📊 角色 ${roleId} 权限数量: ${count} (总权限: ${permissions.length}, 功能权限: ${count})`);
        return count;
      } else {
        console.warn(`⚠️ 角色 ${roleId} 获取权限失败:`, response.message);
        return 0;
      }
    } catch (error) {
      console.error(`❌ 获取角色 ${roleId} 权限数量失败:`, error);
      return 0;
    }
  },

  // 批量获取多个角色的权限数量
  getBatchRolePermissionCounts: async (roleIds: string[]): Promise<Record<string, number>> => {
    try {
      // 并行获取所有角色的权限数量
      const promises = roleIds.map(async (roleId) => {
        try {
          const count = await RolePermissionService.getRolePermissionCount(roleId);
          return { roleId, count };
        } catch (error) {
          console.error(`获取角色 ${roleId} 权限数量失败:`, error);
          return { roleId, count: 0 };
        }
      });

      const results = await Promise.all(promises);
      const counts: Record<string, number> = {};
      results.forEach(({ roleId, count }) => {
        counts[roleId] = count;
      });

      return counts;
    } catch (error) {
      console.error("批量获取角色权限数量失败:", error);
      return {};
    }
  },
};
